import { useCallback, useEffect, useRef, useState } from 'react';
import {
  activeTakeOf,
  emptyNarrationTrack,
  formatDuration,
  narrationSummary,
  type NarrationTake,
  type NarrationTrack,
} from '@slideforge/shared';
import { useStrings } from '../i18n/context.js';
import type { SlideForgeState } from '../state/useSlideForge.js';
import {
  audioStoreAvailable,
  deleteClip,
  getClip,
  putClip,
  usedBytes,
} from '../audio/blob-store.js';
import { exportPortableNarration, importPortableNarration } from '../audio/portable-bundle.js';
import {
  listMicrophones,
  measureDuration,
  MicrophoneError,
  prepareMicrophone,
  recordingSupported,
  startRecording,
  type AudioDevice,
  type RecorderHandle,
} from '../audio/recorder.js';
import { TtsPanel } from './TtsPanel.js';

type Phase = 'idle' | 'counting' | 'recording' | 'saving';

export function NarrationPanel({ state }: { readonly state: SlideForgeState }) {
  const t = useStrings();
  const { selectedSlideId, course, slides, setSlideData } = state;

  const [devices, setDevices] = useState<readonly AudioDevice[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [phase, setPhase] = useState<Phase>('idle');
  const [countdown, setCountdown] = useState(0);
  const [level, setLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | undefined>(undefined);
  const [localBytes, setLocalBytes] = useState(0);
  const [portabilityStatus, setPortabilityStatus] = useState<string | undefined>(undefined);

  const handleRef = useRef<RecorderHandle | undefined>(undefined);
  const startedAtRef = useRef(0);
  const bundleInputRef = useRef<HTMLInputElement | null>(null);

  const track = selectedSlideId ? course.slides[selectedSlideId]?.narration : undefined;
  const supported = recordingSupported() && audioStoreAvailable();

  useEffect(() => {
    void listMicrophones().then(setDevices).catch(() => undefined);
  }, [phase]);

  useEffect(() => {
    void usedBytes().then(setLocalBytes).catch(() => undefined);
  }, [course]);

  // Stop the microphone if the pane goes away mid-take. A live capture that
  // outlives its UI leaves the recording indicator on with nothing to stop it.
  useEffect(() => () => handleRef.current?.cancel(), []);

  const saveTrack = useCallback(
    async (next: NarrationTrack) => {
      if (!selectedSlideId) return;
      await setSlideData(selectedSlideId, 'narration', next);
    },
    [selectedSlideId, setSlideData],
  );

  /**
   * The meter loop, tied to the recording phase rather than started and stopped
   * by hand. A self-referential `requestAnimationFrame` callback has to outlive
   * every early return in `begin`/`finish`/`discard`; scoping it to an effect
   * means the frame is cancelled by the same thing that started it.
   */
  useEffect(() => {
    if (phase !== 'recording') return;
    let frame = 0;
    const tick = () => {
      const handle = handleRef.current;
      if (handle) {
        setLevel(handle.level());
        setElapsedMs(Date.now() - startedAtRef.current);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  const begin = useCallback(async () => {
    if (!selectedSlideId) return;
    setError(undefined);

    // Office on the web withholds the microphone from the add-in iframe until
    // the author answers an Office permission dialog. Ask before the countdown:
    // a countdown running behind a modal is a take that starts mid-word.
    //
    // A refusal here does not stop the attempt. Office reports "denied" whether
    // the author pressed Deny or no dialog was ever shown, and the frame may
    // already hold the permission from an earlier grant, so the browser decides
    // and whatever Office said travels with the error if capture does fail.
    let permissionNote: string | undefined;
    try {
      const prepared = await prepareMicrophone();
      permissionNote = prepared.note;
      if (prepared.reloadRequired) {
        // A first-time grant only reaches a newly created frame, so the add-in
        // has to reload before recording can start.
        setPhase('idle');
        setError(t.micReloading);
        try {
          globalThis.location.reload();
        } catch {
          // A host that forbids reload leaves the message on screen instead.
        }
        return;
      }
    } catch (caught) {
      // Asking must never be the thing that stops a recording.
      permissionNote = caught instanceof Error ? caught.message : String(caught);
    }

    // A countdown is the difference between narration that starts mid-word and
    // narration that starts cleanly.
    setPhase('counting');
    for (let n = 3; n > 0; n -= 1) {
      setCountdown(n);
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    setCountdown(0);

    try {
      const handle = await startRecording(deviceId, { permissionNote });
      handleRef.current = handle;
      startedAtRef.current = Date.now();
      setPhase('recording');
    } catch (caught) {
      setPhase('idle');
      setError(caught instanceof MicrophoneError ? caught.message : String(caught));
    }
  }, [selectedSlideId, deviceId, t]);

  const finish = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle) return;
    setPhase('saving');
    handle.stop();

    try {
      const blob = await handle.done;
      handleRef.current = undefined;
      const [clip, durationMs] = await Promise.all([putClip(blob), measureDuration(blob)]);

      const take: NarrationTake = {
        id: `take-${Date.now().toString(36)}`,
        source: 'recorded',
        mimeType: clip.mimeType,
        durationMs,
        byteLength: clip.byteLength,
        audioId: clip.audioId,
        recordedAt: new Date().toISOString(),
        label: '',
      };

      const current = track ?? emptyNarrationTrack();
      // A new take becomes active: recording again almost always means the
      // author wants the new one.
      await saveTrack({ ...current, takes: [...current.takes, take], activeTakeId: take.id });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setPhase('idle');
      setLevel(0);
      setElapsedMs(0);
    }
  }, [track, saveTrack]);

  const discard = useCallback(() => {
    handleRef.current?.cancel();
    handleRef.current = undefined;
    setPhase('idle');
    setLevel(0);
    setElapsedMs(0);
  }, []);

  const exportBundle = useCallback(async () => {
    setError(undefined);
    setPortabilityStatus(undefined);
    try {
      const result = await exportPortableNarration(course);
      downloadBlob(result.blob, result.filename);
      setPortabilityStatus(t.narrationExported(result.assetCount));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [course, t]);

  const importBundle = useCallback(async (file: File) => {
    setError(undefined);
    setPortabilityStatus(undefined);
    try {
      const count = await importPortableNarration(file, course.courseId);
      setLocalBytes(await usedBytes());
      setPortabilityStatus(t.narrationImported(count));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [course.courseId, t]);

  const summary = narrationSummary(
    new Map(slides.map((slide) => [slide.id, course.slides[slide.id]?.narration])),
    slides.map((slide) => slide.id),
  );

  if (!selectedSlideId) {
    return (
      <section aria-labelledby="sf-narration-heading" className="sf-panel">
        <h2 id="sf-narration-heading">{t.narrationHeading}</h2>
        <p>{t.noSlideSelected}</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="sf-narration-heading" className="sf-panel">
      <h2 id="sf-narration-heading">{t.narrationHeading}</h2>

      <TtsPanel state={state} track={track} onSave={saveTrack} />

      <p className="sf-note">{t.narrationLocalOnly}</p>

      <section aria-labelledby="sf-narration-portability-heading" className="sf-card">
        <h3 id="sf-narration-portability-heading">{t.narrationPortabilityHeading}</h3>
        <p>{t.narrationPortabilityBody}</p>
        <div className="sf-actions">
          <button type="button" onClick={() => void exportBundle()}>
            {t.narrationExportBundle}
          </button>
          <button type="button" onClick={() => bundleInputRef.current?.click()}>
            {t.narrationImportBundle}
          </button>
          <input
            ref={bundleInputRef}
            hidden
            type="file"
            accept=".zip,.slideforge-audio.zip,application/zip"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void importBundle(file);
              event.target.value = '';
            }}
          />
        </div>
        {portabilityStatus && <p role="status">{portabilityStatus}</p>}
      </section>

      {error && (
        <p role="alert" className="sf-error">
          {error}
        </p>
      )}

      {!supported ? (
        <p role="alert" className="sf-error">
          {t.narrationUnsupported}
        </p>
      ) : (
        <>
          <label className="sf-field">
            <span>{t.microphone}</span>
            <select
              value={deviceId ?? ''}
              disabled={phase !== 'idle'}
              onChange={(e) => setDeviceId(e.target.value || undefined)}
            >
              <option value="">{t.defaultMicrophone}</option>
              {devices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </label>

          {phase === 'counting' && (
            <p className="sf-countdown" aria-live="assertive">
              {countdown}
            </p>
          )}

          {phase === 'recording' && (
            <div className="sf-recording">
              <div
                className="sf-meter"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={1}
                aria-valuenow={Number(level.toFixed(2))}
                aria-label={t.inputLevel}
              >
                <div className="sf-meter-fill" style={{ width: `${Math.round(level * 100)}%` }} />
              </div>
              <span className="sf-elapsed">{formatDuration(elapsedMs)}</span>
              {/*
                Silence during a take is the failure this meter exists to catch:
                a muted or wrong input records a perfectly normal-looking take
                that turns out to be empty only after publishing.
              */}
              {elapsedMs > 2500 && level < 0.01 && (
                <p role="alert" className="sf-warning">
                  {t.narrationSilent}
                </p>
              )}
            </div>
          )}

          <div className="sf-actions">
            {phase === 'idle' && (
              <button type="button" className="sf-primary" onClick={() => void begin()}>
                {t.recordNarration}
              </button>
            )}
            {phase === 'recording' && (
              <>
                <button type="button" className="sf-primary" onClick={() => void finish()}>
                  {t.stopRecording}
                </button>
                <button type="button" onClick={discard}>
                  {t.discardTake}
                </button>
              </>
            )}
            {phase === 'saving' && <span>{t.savingTake}</span>}
          </div>
        </>
      )}

      <TakeList track={track} onChange={(next) => void saveTrack(next)} />

      <dl className="sf-stats">
        <div className="sf-stat">
          <dt>{t.narrationTotal}</dt>
          <dd>{formatDuration(summary.totalMs)}</dd>
        </div>
        <div className="sf-stat">
          <dt>{t.narrationSilentSlides}</dt>
          <dd>{summary.silentSlideIds.length}</dd>
        </div>
        <div className="sf-stat">
          <dt>{t.narrationLocalBytes}</dt>
          <dd>{formatBytes(localBytes)}</dd>
        </div>
      </dl>
    </section>
  );
}

function TakeList({
  track,
  onChange,
}: {
  readonly track: NarrationTrack | undefined;
  readonly onChange: (next: NarrationTrack) => void;
}) {
  const t = useStrings();
  const [playing, setPlaying] = useState<string | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined);
  const urlRef = useRef<string | undefined>(undefined);

  const release = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = undefined;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = undefined;
    setPlaying(undefined);
  }, []);

  useEffect(() => release, [release]);

  const play = useCallback(
    async (take: NarrationTake) => {
      release();
      const clip = await getClip(take.audioId);
      if (!clip) {
        setPlaying(undefined);
        return;
      }
      const url = URL.createObjectURL(clip.blob);
      urlRef.current = url;
      const audio = new Audio(url);
      audio.onended = release;
      audioRef.current = audio;
      setPlaying(take.id);
      await audio.play().catch(() => release());
    },
    [release],
  );

  if (!track || track.takes.length === 0) {
    return <p className="sf-note">{t.noTakesYet}</p>;
  }

  const active = activeTakeOf(track);

  return (
    <ol className="sf-takes">
      {track.takes.map((take, index) => {
        const isActive = active?.id === take.id;
        return (
          <li key={take.id} className={isActive ? 'sf-take sf-take-active' : 'sf-take'}>
            <span className="sf-take-label">
              {take.label || t.takeNumber(index + 1)} · {formatDuration(take.durationMs)}
            </span>

            <button type="button" onClick={() => (playing === take.id ? release() : void play(take))}>
              {playing === take.id ? t.stopPlayback : t.playTake}
            </button>

            <button
              type="button"
              aria-pressed={isActive}
              onClick={() => onChange({ ...track, activeTakeId: take.id })}
            >
              {isActive ? t.activeTake : t.useThisTake}
            </button>

            <button
              type="button"
              className="sf-danger"
              onClick={() => {
                const takes = track.takes.filter((x) => x.id !== take.id);
                const stillUsed = takes.some((x) => x.audioId === take.audioId);
                if (!stillUsed) void deleteClip(take.audioId).catch(() => undefined);
                onChange({
                  ...track,
                  takes,
                  ...(track.activeTakeId === take.id
                    ? { activeTakeId: takes[takes.length - 1]?.id }
                    : {}),
                });
              }}
            >
              {t.deleteTake}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
